import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { createSubstitutionRequestByAdmin, getAdminSession, getAdminSessions } from '@/lib/api-calls';
import { stringIfNull } from '@/lib/string-utils';
import { getOriginalTeacherIssueLabel } from '@airreading/common';
import {
  AdminSession,
  SubstitutionRequestType,
  originalTeacherIssueSelectableSchema,
  originalTeacherIssueSelectableValues,
} from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DefaultValues } from 'react-hook-form';
import { z } from 'zod';

interface CreateSubstitutionRequestByAdminProps {
  session: AdminSession;
  coachIdToCreateSubRequestFor: string;
  trigger?: React.ReactNode;
}

const formSchema = z.object({
  originalTeacherClassIssue: originalTeacherIssueSelectableSchema,
});

type FormValues = z.infer<typeof formSchema>;

export function CreateSubstitutionRequestByAdmin({
  session,
  coachIdToCreateSubRequestFor,
  trigger,
}: CreateSubstitutionRequestByAdminProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: createSubstitutionRequestByAdmin,
  });

  const onSubmit = async ({ closeModal, formData }: SubmitProps<FormValues>) => {
    try {
      const hasOriginalTeacherClassIssue =
        !!formData.originalTeacherClassIssue && formData.originalTeacherClassIssue !== 'null';
      await mutateAsync({
        coachId: coachIdToCreateSubRequestFor,
        sessionId: session.id,
        type: hasOriginalTeacherClassIssue
          ? SubstitutionRequestType.coach_no_show
          : SubstitutionRequestType.assigned_by_admin,
      });

      toast({
        title: 'Success',
        description: 'Substitution request created successfully',
      });

      queryClient.invalidateQueries(getAdminSession(session.id));
      queryClient.invalidateQueries(getAdminSessions());

      closeModal();
    } catch (error) {
      console.error('Failed to create substitution request:', error);
      toast({
        title: 'Error',
        description: 'Failed to create substitution request',
        variant: 'destructive',
      });
    }
  };

  const defaultValues: DefaultValues<FormValues> = {
    originalTeacherClassIssue: stringIfNull(session.originalTeacherIssue),
  };

  return (
    <FormModal
      modalTitle="Create Substitution Request"
      formSchema={formSchema}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      trigger={trigger || <Button variant="outline">Create sub request</Button>}
      confirmBtnLabel="Create sub request"
    >
      {({ form }) => (
        <div className="space-y-4 py-4">
          <FormSingleSelect
            form={form}
            name="originalTeacherClassIssue"
            label="Original teacher class issue"
            options={originalTeacherIssueSelectableValues}
            renderOption={getOriginalTeacherIssueLabel}
            triggerProps={{ className: 'w-full' }}
          />
        </div>
      )}
    </FormModal>
  );
}
